import {UI} from "../../../../../../stem-core/src/ui/UIBase";
import {StyleSheet} from "../../../../../../stem-core/src/ui/Style";

import {BlinkInput, BlinkInputStyle} from "../../../../../ui/Input";
import {Messages} from "../../../../../Messages";
import {AmExIcon, MasterCardIcon, VisaIcon} from "../../../../../SVGElements";
import {styleRule, styleRuleInherit} from "../../../../../../stem-core/src/decorators/Style";
import {
    StripeCardInput,
    StripeCardInputStyle
} from "../../../../../website/authenticated/pages/wallet/components/cards/CardInput";
import {registerStyle} from "../../../../../../stem-core/src/ui/style/Theme";
import {NOOP_FUNCTION} from "../../../../../Utils";
import {PaymentMethodStore} from "../../../../../State";
import {Flow} from "../../../../initializeFlows";
import {STRIPE_CARD_ERROR_MAX_LENGTH} from "../../../../../Constants";
import {isFirefox} from "../../../../../Utils";
import {isSmallScreen} from "../../../../../UtilsLib";
import {apiAddPaymentMethodToken} from "../../../../../../client/state/PaymentMethodStore";
import {iFrameMerchantService} from "blinkpay/services/IframeMerchantService";
import {BlinkCardInput} from "../../../../../website/authenticated/pages/wallet/components/cards/BlinkCardInput";
import {Controller} from "../../../../Controller";


export class CardInputStyle extends StripeCardInputStyle {
    @styleRuleInherit
    element = {
        position: "relative",
        overflow: "initial",
    };

    @styleRuleInherit
    inputContainer = {
        background: this.themeProps.MERCHANT_INPUT_BACKGROUND,
        borderRadius: this.themeProps.MERCHANT_BUTTON_INPUT_TOAST_BORDER_RADIUS,
        padding: isSmallScreen() ? "0.25rem 5px 0.25rem 12px" : "",
        border: () => "1px solid " + this.themeProps.MERCHANT_INPUT_BORDER_COLOR,
        height: 42,
        transition: "box-shadow 0.2s ease",
        ...this.themeProps.MERCHANT_INPUT_STYLE,
    };

    @styleRuleInherit
    focused = {
        border: () => "1px solid " + this.themeProps.MERCHANT_INPUT_BORDER_ACTIVE_COLOR,
    };

    @styleRuleInherit
    error = {
        borderColor: this.themeProps.MERCHANT_ERROR,
        ":hover": {
            borderColor: this.themeProps.MERCHANT_ERROR + " !important",
        },
        " input": {
            color: this.themeProps.MERCHANT_ERROR + " !important",
        },
        " input::placeholder": {
            color: this.themeProps.MERCHANT_ERROR + " !important",
        }
    };

    @styleRuleInherit
    errorMessage = {
        position: "absolute",
        top: -6,
        paddingBottom: 0,
        left: 0,
        transform: "translateY(calc(-100%))",
        fontWeight: this.themeProps.MERCHANT_FONT_WEIGHT_BOLD,
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_SMALL,
        color: this.themeProps.MERCHANT_ERROR,
        marginTop: 0,
    };
}

@registerStyle(CardInputStyle)
export class CardInput extends StripeCardInput {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            autofocus: false,
            stripeStyle: {
                base: {
                    fontSize: this.styleSheet.themeProps.MERCHANT_FONT_SIZE_LARGE,
                    color: this.styleSheet.themeProps.MERCHANT_INPUT_TEXT_COLOR,
                    iconColor: this.styleSheet.themeProps.MERCHANT_INPUT_TEXT_COLOR,
                    fontFamily: this.styleSheet.themeProps.MERCHANT_FONT_FAMILY,
                    "::placeholder": {
                        color: this.styleSheet.themeProps.MERCHANT_INPUT_PLACEHOLDER_COLOR,
                        fontWeight: this.styleSheet.themeProps.MERCHANT_FONT_WEIGHT_REGULAR,
                    },
                },
            },
        };
    }

    confirmCardWithoutStoring(scope, callback = NOOP_FUNCTION, errorCallback = NOOP_FUNCTION, data = {}) {
        const {paymentProcessor} = this.options;
        this.updateOptions({error: null});
        this.stripe.createToken(this.card, data).then(async response => {
            if (response.error) {
                this.error = response.error.message.length > STRIPE_CARD_ERROR_MAX_LENGTH ?
                    Messages.genericCardError.toString() : response.error.message;
                this.redraw();
                errorCallback();
            } else {
                this.card.blur();
                try {
                    const addPaymentMethodResponse = await apiAddPaymentMethodToken(
                        response.token,
                        scope,
                        paymentProcessor
                    );
                    callback(addPaymentMethodResponse)
                } catch (error) {
                    this.error = error.message.length > STRIPE_CARD_ERROR_MAX_LENGTH ?
                        Messages.genericCardError.toString() : error.message;
                    this.redraw();
                    errorCallback(error);
                }
            }
        });
    }
}

class CardFormStyle extends StyleSheet {
    @styleRule
    cardIcons = {
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: 10,
    };

    @styleRule
    cardIcon = {
        marginLeft: 8,
    };
}

export class NameInputStyle extends BlinkInputStyle {
    @styleRuleInherit
    input = {
        "-webkit-text-stroke-width": isSmallScreen() ? 0 : ".2px",
        fontSize: () => this.themeProps.MERCHANT_FONT_SIZE_LARGE + " !important",
        "::placeholder": {
            textShadow: (isFirefox() ? "0px" : "0.2px") + `0px 0px ${this.themeProps.MERCHANT_INPUT_PLACEHOLDER_COLOR}`,
            opacity: 1,
        },
    };

    @styleRuleInherit
    error = {
        borderColor: this.themeProps.MERCHANT_ERROR,
        ":hover": {
            borderColor: this.themeProps.MERCHANT_ERROR + " !important",
        },
        " input": {
            color: this.themeProps.MERCHANT_ERROR + " !important",
        },
        " input::placeholder": {
            color: this.themeProps.MERCHANT_ERROR + " !important",
        }
    };
}

@registerStyle(NameInputStyle)
export class NameInput extends BlinkInput {}

@registerStyle(CardFormStyle)
export class CardForm extends UI.Element {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            onChange: NOOP_FUNCTION,
            NameInputClass: NameInput,
            CardInputClass: CardInput,
        }
    }

    render() {
        const {styleSheet} = this;
        const {NameInputClass, CardInputClass} = this.options;
        const creditCardIconSize = 12;

        const CardInput = iFrameMerchantService.isCDSPaymentMethod() ? BlinkCardInput : CardInputClass;

        return [
            <NameInputClass inputAttributes={{placeholder: Messages.addCardNameInput.toString(), autofocus: true}}
                       ref="nameInput" initialValue={Controller.paymentMethod?.cardHolderName}
                       validators={[{
                           condition: () => !this.nameInput.isEmpty()
                       }]} onBlur={() => this.nameInput.validate()} onChange={() => this.options.onChange()}/>,
            <div className={styleSheet.cardIcons}>
                <VisaIcon size={creditCardIconSize} className={styleSheet.cardIcon}/>
                <MasterCardIcon size={creditCardIconSize} className={styleSheet.cardIcon}/>
                <AmExIcon size={creditCardIconSize} className={styleSheet.cardIcon}/>
            </div>,
            <CardInput ref="cardInput" onChange={() => this.options.onChange()}/>,
        ]
    }

    isValid() {
        return this.cardInput.isInputComplete() && this.nameInput.isValid();
    }

    confirmCard(callback, errorCallback) {
        const name = this.nameInput.getValue();
        if (Controller.paymentMethod) {
            Controller.paymentMethod.cardHolderName = name;
        }
        this.cardInput.confirmCard(callback, errorCallback, {name});
    }

    confirmCardWithoutStoring(scope, callback, errorCallback) {
        const name = this.nameInput.getValue();
        if (Controller.paymentMethod) {
            Controller.paymentMethod.cardHolderName = name;
        }
        this.cardInput.confirmCardWithoutStoring(scope, callback, errorCallback, {name});
    }
}
